import { useState, useEffect, useRef } from 'react';
import { questions } from '../data/questions';
import { archetypes } from '../data/archetypes';
import StarField from '../components/StarField';
import styles from './LiveQuiz.module.css';

export default function LiveQuiz({ roomId, onExit }) {
  const [status, setStatus] = useState('connecting'); // connecting, joined, quiz, result, error
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [scores, setScores] = useState({});
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [topArchetype, setTopArchetype] = useState(null);
  const wsRef = useRef(null);

  useEffect(() => {
    // 使用当前页面的hostname来连接WebSocket（手机扫码时会是局域网IP）
    const wsUrl = process.env.NODE_ENV === 'production' 
      ? `wss://${window.location.host}`
      : `ws://${window.location.hostname}:3001`;
    
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'join_room', roomId }));
    };

    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      
      switch (msg.type) {
        case 'joined':
          setStatus('quiz');
          break;
        case 'error':
          setStatus('error');
          break;
        case 'room_closed':
          setStatus('error');
          break;
        default:
          break;
      }
    };

    ws.onerror = () => {
      setStatus('error');
    };

    return () => {
      ws.close();
    };
  }, [roomId]);

  const handleSelect = (option, index) => {
    if (selectedIndex !== null) return;
    
    setSelectedIndex(index);
    
    const newScores = { ...scores };
    const { archetype, weight } = option;
    newScores[archetype] = (newScores[archetype] || 0) + weight;
    setScores(newScores);

    // 发送进度更新
    const progress = ((currentQuestion + 1) / questions.length) * 100;
    wsRef.current?.send(JSON.stringify({ 
      type: 'progress_update', 
      progress 
    }));

    setTimeout(() => {
      if (currentQuestion >= questions.length - 1) {
        // 完成测试，计算结果
        const top = Object.entries(newScores)
          .sort((a, b) => b[1] - a[1])[0];
        const topArch = top ? top[0] : 'explorer';
        setTopArchetype(topArch);
        
        // 发送结果到主屏幕
        wsRef.current?.send(JSON.stringify({
          type: 'submit_result',
          scores: newScores,
          topArchetype: topArch,
          archetypeInfo: archetypes[topArch]
        }));
        
        setStatus('result');
      } else {
        setCurrentQuestion(prev => prev + 1);
        setSelectedIndex(null);
      }
    }, 600);
  };

  // 连接中
  if (status === 'connecting') {
    return (
      <div className={styles.container}>
        <StarField density={80} speed={0.2} />
        <div className={styles.statusScreen}>
          <div className={styles.spinner} />
          <p>正在加入房间 {roomId}...</p>
        </div>
      </div>
    );
  }

  // 错误状态
  if (status === 'error') {
    return (
      <div className={styles.container}>
        <StarField density={80} speed={0.2} />
        <div className={styles.statusScreen}>
          <div className={styles.errorIcon}>❌</div>
          <p>房间不存在或已关闭</p>
          <button className={styles.retryBtn} onClick={onExit}>
            返回首页
          </button>
        </div>
      </div>
    );
  }

  // 结果页
  if (status === 'result' && topArchetype) {
    const archetype = archetypes[topArchetype] || {};
    return (
      <div className={styles.container}>
        <StarField density={80} speed={0.2} />
        <div className={styles.resultScreen}>
          <div className={styles.resultPlanet}>{archetype.planet || '🪐'}</div>
          <h1 className={styles.resultTitle}>你的行星原型</h1>
          <h2 className={styles.resultName}>{archetype.name || topArchetype}</h2>
          <p className={styles.resultDesc}>{archetype.feature?.slice(0, 100)}...</p>
          <p className={styles.resultHint}>你的结果已同步到大屏幕！</p>
          <button className={styles.againBtn} onClick={onExit}>
            再测一次
          </button>
        </div>
      </div>
    );
  }

  // 答题页
  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className={styles.container}>
      <StarField density={80} speed={0.2} />
      
      {/* 进度条 */}
      <div className={styles.progressBar}>
        <div className={styles.progressFill} style={{ width: `${progress}%` }} />
        <span className={styles.progressText}>
          {currentQuestion + 1} / {questions.length}
        </span>
      </div>

      {/* 题目 */}
      <div className={styles.questionCard}>
        <div className={styles.questionNumber}>Q{currentQuestion + 1}</div>
        <p className={styles.questionText}>{question.text}</p>
        
        <div className={styles.options}>
          {question.options.map((opt, i) => (
            <button
              key={i}
              className={`${styles.option} ${selectedIndex === i ? styles.selected : ''}`}
              onClick={() => handleSelect(opt, i)}
              disabled={selectedIndex !== null}
            >
              <span className={styles.optionLetter}>
                {String.fromCharCode(65 + i)}
              </span>
              <span className={styles.optionText}>{opt.text}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
